import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router-dom";

import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import { request, requestSilentNoRetry } from "@/Api";
import {
    AuthContainer,
    AuthSection,
    Heading,
    SubHeading,
} from "@/auth/StyledComponents";
import { GlobalStyle } from "@/themes";
import { MakeOSIcon, PageTitle } from "@/common/CommonComponents";
import { selectEmail, selectIsVerified, setToken } from "@/auth/authSlice";
import AuthNav from "@/auth/AuthNav";

export default function Verify() {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const status = params["*"];

    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const email = useSelector(selectEmail);
    const isAccountVerified = useSelector(selectIsVerified);

    let heading = "Almost done!";
    let subHeading =
        "To complete your registration, please check your inbox for a verification email.";
    if (status && status.includes("error")) {
        heading = "Sorry, we could not verify your account";
        subHeading = "The link you used is invalid or expired.";
    }

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleSend = () => {
        setLoading(true);
        request.post("/resend-verification-email", { email }).finally(() => {
            setSent(true);
            setLoading(false);
        });
    };

    useEffect(() => {
        if (isAccountVerified) {
            navigate("/");
        }
    }, [isAccountVerified]);

    useEffect(() => {
        requestSilentNoRetry
            .post("/refresh-token")
            .then((response) => {
                dispatch(setToken(response.data));
            })
            .catch(() => {
                navigate("/logout");
            });
    }, []);

    return (
        <AuthContainer isMobile={isMobile}>
            <Helmet>
                <PageTitle title="Verify Account" />
            </Helmet>
            <GlobalStyle theme={theme.palette.mode} />
            <AuthNav internalLinkLabel="Logout" internalLinkUrl="/logout" />

            <AuthSection isMobile={isMobile}>
                <Grid
                    item
                    container
                    direction="column"
                    alignItems="center"
                    spacing={2}
                >
                    <Grid
                        item
                        sx={{ marginBottom: isMobile ? "24px" : "48px" }}
                    >
                        <MakeOSIcon width={222} />
                    </Grid>
                    <Grid item>
                        <Heading>{heading}</Heading>
                    </Grid>
                    <Grid item>
                        <SubHeading>{subHeading}</SubHeading>
                    </Grid>
                    <Grid item>
                        {sent ? (
                            <span>Sent!</span>
                        ) : (
                            <Button
                                onClick={handleSend}
                                disabled={loading || sent}
                            >
                                Resend verification email
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </AuthSection>
        </AuthContainer>
    );
}
